import React, { useState, useEffect } from "react";
import { ThemeProvider as StyledThemeProvider, DefaultTheme } from "styled-components";
import { ConfigProvider } from "antd";
import moment from "moment";
import { useDispatch, useSelector } from "react-redux";
import { useTheme } from "../../../context/ThemeContext";
import { getAntdThemeConfig } from "../../../utils/antdTheme";
import { fetchDeliveryCostAnalysis, DeliveryCostAnalysisRequestParams } from "./store/deliveryCostAnalysisSlice";
import { RootState } from "../../../redux/store";
import { MainContainer, FullWidthSpace } from "./styles";
import DeliveryCostAnalysisHeader from "./DeliveryCostAnalysisHeader";
import DeliverySummary from "./DeliverySummary";
import DeliveryInsights from "./DeliveryInsights";
import DeliveryChartSection from "./DeliveryChartSection";
import { DateRange, Merchant, Store, User, MetricOption } from "./types";

const DeliveryCostAnalysis: React.FC = () => {
  const { isDarkMode } = useTheme();
  const dispatch = useDispatch();
  const { deliveryData, loading } = useSelector(
    (state: RootState) => state.deliveryCostAnalysis
  );
  const [merchant, setMerchant] = useState<Merchant | null>(null);
  const [isFilter, setIsFilter] = useState<boolean>(false);
  const [metricOption, setMetricOption] = useState<MetricOption>("profit");
  const [selectedDateRange, setSelectedDateRange] = useState<DateRange>({
    startDate: moment().subtract(7, "days"),
    endDate: moment(),
  });
  const [chartType, setChartType] = useState<string>("line");
  const [selectedFilter] = useState<string>("");
  const [storeId, setStoreId] = useState<string>("");
  const user: User = JSON.parse(localStorage.getItem("user") || "{}");
  const themeMode: "light" | "dark" = isDarkMode ? "dark" : "light";
  const styledTheme = { mode: themeMode } as DefaultTheme;

  useEffect(() => {
    if (user?.role === "merchant" && !merchant) {
      setMerchant(user as Merchant);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.role]);

  useEffect(() => {
    const params: DeliveryCostAnalysisRequestParams = isFilter
      ? {
          filter: selectedFilter,
          ...(storeId && { store: storeId }),
        }
      : {
          startDate: moment(selectedDateRange.startDate).format("YYYY-MM-DD"),
          endDate: moment(selectedDateRange.endDate).format("YYYY-MM-DD"),
          ...(merchant?.id && { merchantId: merchant.id }),
          ...(storeId && { store: storeId }),
        };

    dispatch(fetchDeliveryCostAnalysis(params) as any);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedFilter, selectedDateRange, isFilter, merchant, storeId, dispatch]);

  const handleDateRangeChange = (range: DateRange) => {
    setIsFilter(false);
    setSelectedDateRange(range);
  };

  const handleDateSelect = (selection: DateRange) => {
    setSelectedDateRange(selection);
  };

  const handleMerchantChange = (merch: Merchant) => {
    setMerchant(merch);
  };

  const handleStoreChange = (store: Store) => {
    setStoreId(store.id);
  };

  const handleMetricChange = (option: string) => {
    setMetricOption(option as MetricOption);
  };

  return (
    <StyledThemeProvider theme={styledTheme}>
      <ConfigProvider theme={getAntdThemeConfig(isDarkMode)}>
        <MainContainer>
          <FullWidthSpace direction="vertical" size="large">
            <DeliveryCostAnalysisHeader
              merchant={merchant}
              storeId={storeId}
              user={user}
              onMerchantChange={handleMerchantChange}
              onStoreChange={handleStoreChange}
            />

            <DeliverySummary merchant={merchant} storeId={storeId} />

            <DeliveryInsights deliveryData={deliveryData} />

            <DeliveryChartSection
              deliveryData={deliveryData}
              selectedDateRange={selectedDateRange}
              chartType={chartType}
              metricOption={metricOption}
              isLoading={loading}
              onDateRangeChange={handleDateRangeChange}
              onDateSelect={handleDateSelect}
              onMetricChange={handleMetricChange}
              onChartTypeChange={setChartType}
            />
          </FullWidthSpace>
        </MainContainer>
      </ConfigProvider>
    </StyledThemeProvider>
  );
};

export default DeliveryCostAnalysis;
